/**
 * Project Detail Components
 * Combined file containing all project detail components and exports
 */

import { Modal, Form, Button, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import Flatpickr from 'react-flatpickr';

/**
 * AddProjectTaskModal - Modified Add Task Modal for Project Tasks
 * 
 * Changes from original:
 * - Removed: Select a Project, Privacy
 * - Changed labels: Task List -> Department, Assignees -> Assign To, Due Date -> End Date
 * - Reordered fields
 */
export const AddProjectTaskModal = ({
    show,
    onHide,
    taskName,
    setTaskName,
    selectedDepartment,
    setSelectedDepartment,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    estimatedTime,
    setEstimatedTime,
    selectedPriority,
    setSelectedPriority,
    selectedAssignees,
    setSelectedAssignees,
    description,
    setDescription,
    notifyByEmail,
    setNotifyByEmail,
    departmentOptions,
    priorityOptions,
    assigneeOptions,
    onSubmit
}) => {
    return (
        <Modal show={show} onHide={onHide} backdrop="static" keyboard={false} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title className='display-1'>Add New Task</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Row className='gy-3'>
                        {/* Task Name */}
                        <Col sm={6}>
                            <Form.Group>
                                <Form.Label>Task Name <span className='text-danger'>*</span></Form.Label>
                                <Form.Control 
                                    type="text"
                                    value={taskName}
                                    onChange={(e) => setTaskName(e.target.value)}
                                    placeholder="Enter task name"
                                />
                            </Form.Group>
                        </Col>

                        {/* Select Department */}
                        <Col sm={6}>
                            <Form.Group>
                                <Form.Label>Select Department <span className='text-danger'>*</span></Form.Label>
                                <Select
                                    value={selectedDepartment}
                                    onChange={setSelectedDepartment}
                                    options={departmentOptions}
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    placeholder="Select department"
                                    isClearable
                                />
                            </Form.Group>
                        </Col>

                        {/* Start Date */}
                        <Col sm={6}>
                            <Form.Group>
                                <Form.Label>Start Date <span className='text-danger'>*</span></Form.Label>
                                <Flatpickr
                                    value={startDate}
                                    onChange={([date]) => setStartDate(date)}
                                    className="form-control"
                                    options={{
                                        dateFormat: 'd M Y',
                                        allowInput: true,
                                        disableMobile: true,
                                        enableTime: false,
                                        time_24hr: false
                                    }}
                                />
                            </Form.Group>
                        </Col>

                        {/* End Date */}
                        <Col sm={6}>
                            <Form.Group>
                                <Form.Label>End Date <span className='text-danger'>*</span></Form.Label>
                                <Flatpickr
                                    value={endDate}
                                    onChange={([date]) => setEndDate(date)}
                                    className="form-control"
                                    options={{
                                        dateFormat: 'd M Y',
                                        minDate: startDate,
                                        allowInput: true,
                                        disableMobile: true,
                                        enableTime: false,
                                        time_24hr: false
                                    }}
                                />
                            </Form.Group>
                        </Col>

                        {/* Estimated Time */}
                        <Col sm={6}>
                            <Form.Group>
                                <Form.Label>Estimated Time</Form.Label>
                                <Form.Control 
                                    type="text"
                                    value={estimatedTime}
                                    onChange={(e) => setEstimatedTime(e.target.value)}
                                    placeholder="e.g., 4h 30m"
                                />
                            </Form.Group>
                            <Form.Text className="p-tag fs-12">
                                Please enter the estimated time in hours and minutes (e.g., 4h 30m).
                            </Form.Text>
                            <div className='d-flex justify-content-start mt-0'>
                                <Button 
                                    variant="link" 
                                    className='text-decoration-none me-2'
                                    onClick={() => setEstimatedTime('2h')}
                                >
                                    2h
                                </Button>
                                <Button 
                                    variant="link" 
                                    className='text-decoration-none me-2'
                                    onClick={() => setEstimatedTime('1h')}
                                >
                                    1h
                                </Button>
                                <Button 
                                    variant="link" 
                                    className='text-decoration-none me-2'
                                    onClick={() => setEstimatedTime('45m')}
                                >
                                    45m
                                </Button>
                                <Button 
                                    variant="link" 
                                    className='text-decoration-none me-2'
                                    onClick={() => setEstimatedTime('30m')}
                                >
                                    30m
                                </Button>
                                <Button 
                                    variant="link" 
                                    className='text-decoration-none'
                                    onClick={() => setEstimatedTime('15m')}
                                >
                                    15m
                                </Button>
                            </div>
                        </Col>

                        {/* Priority */}
                        <Col sm={6}>
                            <Form.Group>
                                <Form.Label>Priority <span className='text-danger'>*</span></Form.Label>
                                <Select
                                    value={selectedPriority}
                                    onChange={setSelectedPriority}
                                    options={priorityOptions}
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    placeholder="Select priority"
                                    isClearable
                                />
                            </Form.Group>
                        </Col>

                        {/* Assign To */}
                        <Col sm={12}>
                            <Form.Group>
                                <Form.Label>Assign To <span className='text-danger'>*</span></Form.Label>
                                <Select
                                    value={selectedAssignees}
                                    onChange={setSelectedAssignees}
                                    options={assigneeOptions}
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    placeholder="Select team members"
                                    isClearable
                                    isMulti
                                />
                            </Form.Group>
                        </Col>

                        {/* Description */}
                        <Col sm={12}>
                            <Form.Group>
                                <Form.Label>Description</Form.Label>
                                <Form.Control 
                                    as="textarea" 
                                    rows={3}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Enter task description"
                                />
                            </Form.Group>
                        </Col>

                        {/* Notify by Email */}
                        <Col sm={12}>
                            <Form.Group>
                                <Form.Check
                                    type="checkbox"
                                    label="Notify assignees by email"
                                    checked={notifyByEmail}
                                    onChange={(e) => setNotifyByEmail(e.target.checked)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </Form>
            </Modal.Body>
            <Modal.Footer className='d-flex justify-content-between'>
                <Button variant="link" className='text-danger text-decoration-none' onClick={onHide}>
                    Cancel
                </Button>
                <div>
                    <Button variant="primary" className='me-3' onClick={onSubmit}>
                        Create & Another
                    </Button>
                    <Button variant="primary" onClick={() => {
                        onSubmit();
                        onHide();
                    }}>
                        Add Task
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};
